import React, { useState, useEffect, useMemo } from 'react';
import { Download, Trash2, RefreshCw, Lock, Filter, Users, ArrowUpDown, BarChart3 } from 'lucide-react';

const AdminEmailsPage = () => {
  const [activeTab, setActiveTab] = useState('quiz'); // 'quiz' or 'legacy'
  const [quizLeads, setQuizLeads] = useState([]);
  const [legacyEmails, setLegacyEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState(null);
  
  // Filters
  const [chapterFilter, setChapterFilter] = useState('');
  const [experienceFilter, setExperienceFilter] = useState('');
  const [goalFilter, setGoalFilter] = useState('');
  const [obstacleFilter, setObstacleFilter] = useState('');
  const [supportFilter, setSupportFilter] = useState('');

  const fetchQuizLeads = async () => {
    if (!credentials) return;
    
    setIsLoading(true);
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/quiz-leads?limit=200`;
      if (chapterFilter) url += `&chapter=${encodeURIComponent(chapterFilter)}`;
      if (experienceFilter) url += `&experience=${encodeURIComponent(experienceFilter)}`;
      if (goalFilter) url += `&goal=${encodeURIComponent(goalFilter)}`;
      if (obstacleFilter) url += `&obstacle=${encodeURIComponent(obstacleFilter)}`;
      if (supportFilter) url += `&support_type=${encodeURIComponent(supportFilter)}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setQuizLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Error fetching quiz leads:', error);
    }
    setIsLoading(false);
  };

  const fetchStats = async () => {
    if (!credentials) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quiz-leads/stats`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLegacyEmails = async () => {
    if (!credentials) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/emails?limit=200`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLegacyEmails(data);
      }
    } catch (error) {
      console.error('Error fetching legacy emails:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quiz-leads/stats`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        }
      });
      
      if (response.ok) {
        setCredentials({ username, password });
        setIsLoggedIn(true);
      } else {
        setError('Invalid credentials');
      }
    } catch (error) {
      setError('Connection error');
    }
    setIsLoading(false);
  };

  const handleExport = async () => {
    if (!credentials) return;
    
    const endpoint = activeTab === 'quiz' ? 'quiz-leads/export' : 'emails/export';
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/${endpoint}`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = activeTab === 'quiz' ? 'quiz-leads.csv' : 'email-subscribers.csv';
        a.click();
      }
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  const handleDeleteQuizLead = async (leadId) => {
    if (!window.confirm('Delete this lead?')) return;
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quiz-leads/${leadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });
      fetchQuizLeads();
      fetchStats();
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchQuizLeads();
      fetchStats();
      fetchLegacyEmails();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && activeTab === 'quiz') {
      fetchQuizLeads();
    }
  }, [chapterFilter, experienceFilter, goalFilter, obstacleFilter, supportFilter]);

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: '#ffffff' }}>
        <div className="w-full max-w-sm">
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="text-center mb-8">
              <Lock size={40} className="mx-auto mb-4" style={{ color: '#8b7355' }} />
              <h1 className="text-2xl font-light" style={{ fontFamily: "'Playfair Display', serif", color: '#1a1a1a' }}>
                Admin Access
              </h1>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 text-sm rounded-lg border focus:outline-none"
                style={{ borderColor: '#e8e6e1' }}
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 text-sm rounded-lg border focus:outline-none"
                style={{ borderColor: '#e8e6e1' }}
              />
              {error && <p className="text-sm text-red-500">{error}</p>}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 text-sm tracking-wider rounded-lg transition-all disabled:opacity-50"
                style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
              >
                {isLoading ? 'LOGGING IN...' : 'LOGIN'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#ffffff' }}>
      <div className="max-w-7xl mx-auto px-8 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-light" style={{ fontFamily: "'Playfair Display', serif", color: '#1a1a1a' }}>
              Lead Dashboard
            </h1>
            <p className="text-sm mt-1" style={{ color: '#666' }}>
              {activeTab === 'quiz' ? `${quizLeads.length} quiz leads` : `${legacyEmails.length} email subscribers`}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => activeTab === 'quiz' ? fetchQuizLeads() : fetchLegacyEmails()}
              className="flex items-center gap-2 px-4 py-2 text-xs tracking-wide border rounded transition-all hover:bg-gray-50"
              style={{ borderColor: '#e0e0e0', color: '#1a1a1a' }}
            >
              <RefreshCw size={14} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 text-xs tracking-wide rounded transition-all hover:opacity-90"
              style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
            >
              <Download size={14} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && activeTab === 'quiz' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-6 rounded-lg">
              <p className="text-xs tracking-wider mb-2" style={{ color: '#8b7355' }}>TOTAL LEADS</p>
              <p className="text-3xl font-light" style={{ fontFamily: "'Playfair Display', serif" }}>{stats.total_leads}</p>
            </div>
            <div className="bg-white p-6 rounded-lg">
              <p className="text-xs tracking-wider mb-2" style={{ color: '#8b7355' }}>WATCHED TRAINING</p>
              <p className="text-3xl font-light" style={{ fontFamily: "'Playfair Display', serif" }}>{stats.conversions?.watched_training || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-lg">
              <p className="text-xs tracking-wider mb-2" style={{ color: '#8b7355' }}>CLICKED MENTORSHIP</p>
              <p className="text-3xl font-light" style={{ fontFamily: "'Playfair Display', serif" }}>{stats.conversions?.clicked_mentorship || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-lg">
              <p className="text-xs tracking-wider mb-2" style={{ color: '#8b7355' }}>CLICKED VIP</p>
              <p className="text-3xl font-light" style={{ fontFamily: "'Playfair Display', serif" }}>{stats.conversions?.clicked_vip_systems || 0}</p>
            </div>
          </div>
        )}

        {/* Chapter Distribution */}
        {stats && activeTab === 'quiz' && (
          <div className="bg-white p-6 rounded-lg mb-8">
            <p className="text-xs tracking-wider mb-4" style={{ color: '#8b7355' }}>BY CHAPTER</p>
            <div className="grid grid-cols-4 gap-4">
              {['Break Free', 'Build', 'Grow', 'Scale'].map((chapter, idx) => (
                <div key={chapter} className="text-center">
                  <p className="text-2xl font-light mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {stats.by_chapter?.[chapter] || 0}
                  </p>
                  <p className="text-xs" style={{ color: '#666' }}>Chapter {idx + 1}: {chapter}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('quiz')}
            className="px-4 py-2 text-sm tracking-wider transition-all"
            style={{ 
              borderBottom: activeTab === 'quiz' ? '2px solid #1a1a1a' : '2px solid transparent',
              color: activeTab === 'quiz' ? '#1a1a1a' : '#999'
            }}
          >
            Quiz Leads
          </button>
          <button
            onClick={() => setActiveTab('legacy')}
            className="px-4 py-2 text-sm tracking-wider transition-all"
            style={{ 
              borderBottom: activeTab === 'legacy' ? '2px solid #1a1a1a' : '2px solid transparent',
              color: activeTab === 'legacy' ? '#1a1a1a' : '#999'
            }}
          >
            Legacy Emails
          </button>
        </div>

        {/* Filters (Quiz Tab Only) */}
        {activeTab === 'quiz' && (
          <div className="bg-white p-4 rounded-lg mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <Filter size={16} style={{ color: '#666' }} />
              <select
                value={chapterFilter}
                onChange={(e) => setChapterFilter(e.target.value)}
                className="px-3 py-2 text-sm border rounded"
                style={{ borderColor: '#e8e6e1' }}
              >
                <option value="">All Chapters</option>
                <option value="Break Free">Chapter 1: Break Free</option>
                <option value="Build">Chapter 2: Build</option>
                <option value="Grow">Chapter 3: Grow</option>
                <option value="Scale">Chapter 4: Scale</option>
              </select>
              <select
                value={experienceFilter}
                onChange={(e) => setExperienceFilter(e.target.value)}
                className="px-3 py-2 text-sm border rounded"
                style={{ borderColor: '#e8e6e1' }}
              >
                <option value="">All Experience</option>
                <option value="Complete beginner">Complete beginner</option>
                <option value="I've tried before">I've tried before</option>
                <option value="I've made some sales">I've made some sales</option>
                <option value="I'm already earning online">Already earning</option>
                <option value="I'm ready to scale">Ready to scale</option>
              </select>
              <select
                value={goalFilter}
                onChange={(e) => setGoalFilter(e.target.value)}
                className="px-3 py-2 text-sm border rounded"
                style={{ borderColor: '#e8e6e1' }}
              >
                <option value="">All Goals</option>
                <option value="Making my first online income">First income</option>
                <option value="Replacing my salary">Replace salary</option>
                <option value="Creating another income stream">Another stream</option>
                <option value="Building my personal brand">Personal brand</option>
                <option value="Growing what I've already built">Growing existing</option>
              </select>
              <select
                value={obstacleFilter}
                onChange={(e) => setObstacleFilter(e.target.value)}
                className="px-3 py-2 text-sm border rounded"
                style={{ borderColor: '#e8e6e1' }}
              >
                <option value="">All Obstacles</option>
                <option value="I don't know where to begin">Don't know where to begin</option>
                <option value="I feel overwhelmed">Overwhelmed</option>
                <option value="I don't have enough time">No time</option>
                <option value="I struggle with consistency">Consistency</option>
                <option value="I need better systems">Need systems</option>
                <option value="I need accountability">Need accountability</option>
              </select>
              <select
                value={supportFilter}
                onChange={(e) => setSupportFilter(e.target.value)}
                className="px-3 py-2 text-sm border rounded"
                style={{ borderColor: '#e8e6e1' }}
              >
                <option value="">All Support Types</option>
                <option value="Self-paced">Self-paced</option>
                <option value="Templates">Templates</option>
                <option value="Community">Community</option>
                <option value="Mentorship">Mentorship</option>
                <option value="Done-with-you support">Done-with-you</option>
              </select>
            </div>
          </div>
        )}

        {/* Quiz Leads Table */}
        {activeTab === 'quiz' && (
          <div className="bg-white rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: '#1a1a1a' }}>
                  <th className="px-4 py-4 text-left text-xs tracking-wide font-medium" style={{ color: '#ffffff' }}>Name</th>
                  <th className="px-4 py-4 text-left text-xs tracking-wide font-medium" style={{ color: '#ffffff' }}>Email</th>
                  <th className="px-4 py-4 text-left text-xs tracking-wide font-medium" style={{ color: '#ffffff' }}>Chapter</th>
                  <th className="px-4 py-4 text-left text-xs tracking-wide font-medium" style={{ color: '#ffffff' }}>Experience</th>
                  <th className="px-4 py-4 text-left text-xs tracking-wide font-medium" style={{ color: '#ffffff' }}>Goal</th>
                  <th className="px-4 py-4 text-center text-xs tracking-wide font-medium" style={{ color: '#ffffff' }}>Actions</th>
                  <th className="px-4 py-4 text-right text-xs tracking-wide font-medium" style={{ color: '#ffffff' }}>Delete</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-sm" style={{ color: '#666' }}>Loading...</td>
                  </tr>
                ) : quizLeads.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-sm" style={{ color: '#666' }}>No leads found</td>
                  </tr>
                ) : (
                  quizLeads.map((lead) => (
                    <tr key={lead.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td className="px-4 py-4 text-sm" style={{ color: '#1a1a1a' }}>{lead.first_name}</td>
                      <td className="px-4 py-4 text-sm" style={{ color: '#666' }}>{lead.email}</td>
                      <td className="px-4 py-4">
                        <span 
                          className="px-2 py-1 text-xs rounded"
                          style={{ 
                            backgroundColor: lead.chapter_number === 1 ? '#fef3c7' : 
                                           lead.chapter_number === 2 ? '#dbeafe' : 
                                           lead.chapter_number === 3 ? '#d1fae5' : '#fce7f3',
                            color: '#1a1a1a'
                          }}
                        >
                          {lead.chapter_number}. {lead.assigned_chapter}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm" style={{ color: '#666' }}>{lead.experience_level}</td>
                      <td className="px-4 py-4 text-sm" style={{ color: '#666' }}>{lead.life_change}</td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {lead.watched_free_training && <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#d1fae5' }}>Training</span>}
                          {lead.clicked_mentorship && <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#dbeafe' }}>Mentorship</span>}
                          {lead.clicked_vip_systems && <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#fce7f3' }}>VIP</span>}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={() => handleDeleteQuizLead(lead.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Legacy Emails Table */}
        {activeTab === 'legacy' && (
          <div className="bg-white rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: '#1a1a1a' }}>
                  <th className="px-4 py-4 text-left text-xs tracking-wide font-medium" style={{ color: '#ffffff' }}>Name</th>
                  <th className="px-4 py-4 text-left text-xs tracking-wide font-medium" style={{ color: '#ffffff' }}>Email</th>
                  <th className="px-4 py-4 text-left text-xs tracking-wide font-medium" style={{ color: '#ffffff' }}>Phone</th>
                  <th className="px-4 py-4 text-left text-xs tracking-wide font-medium" style={{ color: '#ffffff' }}>WhatsApp</th>
                  <th className="px-4 py-4 text-left text-xs tracking-wide font-medium" style={{ color: '#ffffff' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {legacyEmails.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-sm" style={{ color: '#666' }}>No emails found</td>
                  </tr>
                ) : (
                  legacyEmails.map((sub) => (
                    <tr key={sub.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td className="px-4 py-4 text-sm" style={{ color: '#1a1a1a' }}>{sub.first_name}</td>
                      <td className="px-4 py-4 text-sm" style={{ color: '#666' }}>{sub.email}</td>
                      <td className="px-4 py-4 text-sm" style={{ color: '#666' }}>{sub.phone || '-'}</td>
                      <td className="px-4 py-4">
                        {sub.join_whatsapp ? (
                          <span className="px-2 py-1 text-xs rounded" style={{ backgroundColor: '#25D366', color: 'white' }}>Yes</span>
                        ) : (
                          <span className="text-xs" style={{ color: '#999' }}>No</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm" style={{ color: '#666' }}>
                        {new Date(sub.subscribed_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailsPage;
